import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, GlobeIcon, DollarSignIcon, ChevronRightIcon, HomeIcon, LayoutGridIcon, TagIcon, TrendingUpIcon, StarIcon, PackageIcon, ShieldIcon, HeartIcon, UserIcon, LogOutIcon, ShoppingBagIcon, MapPinIcon, HeartPulseIcon, ActivityIcon, BrainIcon, ThermometerIcon, PillIcon, StethoscopeIcon, CreditCardIcon, BabyIcon, SparklesIcon, DropletsIcon } from 'lucide-react';
import { LoyaltyOnboardingBottomSheet } from './modals/LoyaltyOnboardingBottomSheet';
import { AuthBottomSheet } from './modals/AuthBottomSheet';
import { LanguageBottomSheet } from './modals/LanguageBottomSheet';
import { LoyaltyCard } from './account/LoyaltyCard';
import { useStore } from '../lib/store';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}
export function MobileMenu({
  isOpen,
  onClose
}: MobileMenuProps) {
  const navigate = useNavigate();
  const {
    language,
    setLanguage
  } = useStore();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [languageBottomSheetOpen, setLanguageBottomSheetOpen] = useState(false);
  const [authBottomSheetOpen, setAuthBottomSheetOpen] = useState(false);
  const [loyaltyBottomSheetOpen, setLoyaltyBottomSheetOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  // Calculate loyalty progress from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (savedData) {
      const data = JSON.parse(savedData);
      const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
      const completedRequired = requiredFields.filter(field => field !== '').length;
      const progress = Math.round(completedRequired / requiredFields.length * 100);
      setLoyaltyProgress(progress);
      // If profile is complete, set loyalty member data
      if (progress === 100) {
        setLoyaltyData({
          memberId: '1234567890',
          pointsBalance: 3250,
          status: 'active',
          tier: 'gold',
          isEnrolled: true
        });
      } else {
        setLoyaltyData({
          isEnrolled: false
        });
      }
    }
  }, [loyaltyBottomSheetOpen]);
  const openAuthModal = () => {
    onClose();
    setAuthBottomSheetOpen(true);
  };
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'ar');
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setIsLoggedIn(true);
    setAuthBottomSheetOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      // Prompt to join loyalty
      setTimeout(() => setLoyaltyBottomSheetOpen(true), 500);
    }
  };
  const handleLoyaltyComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyBottomSheetOpen(false);
    // Update loyalty data to enrolled state
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
  };
  const conditions = [{
    icon: <HeartPulseIcon className="w-4 h-4 text-red-600" data-id="element-3397" data-mp="3u7s4z74evi15" />,
    name: 'Heart Health',
    slug: 'heart-health'
  }, {
    icon: <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-3398" data-mp="707wr5wu3cr4" />,
    name: 'Diabetes',
    slug: 'diabetes'
  }, {
    icon: <BrainIcon className="w-4 h-4 text-purple-600" data-id="element-3399" data-mp="2drz0cmeiji8j" />,
    name: 'Mental Health',
    slug: 'mental-health'
  }, {
    icon: <ThermometerIcon className="w-4 h-4 text-orange-600" data-id="element-3400" data-mp="28u7cq4kwiia5" />,
    name: 'Pain Relief',
    slug: 'pain-relief'
  }, {
    icon: <PillIcon className="w-4 h-4 text-green-600" data-id="element-3401" data-mp="2f7tm4vig7pec" />,
    name: 'Digestive Health',
    slug: 'digestive-health'
  }, {
    icon: <StethoscopeIcon className="w-4 h-4 text-cyan-600" data-id="element-3402" data-mp="2k7z7jty4kfnz" />,
    name: 'Respiratory',
    slug: 'respiratory'
  }];
  const handleConditionClick = (slug: string) => {
    onClose();
    navigate(`/category/${slug}`);
  };
  const handleCategoryClick = (slug: string) => {
    onClose();
    navigate(`/category/${slug}`);
  };
  return <>
      {/* Full Page Menu */}
      <div className={`fixed inset-0 bg-white z-[100] transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col h-full w-full overflow-hidden`} data-id="element-3403" data-mp="21golpr79at2y">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-3404" data-mp="18q6hgmusqlny">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8onJCNEDDvmSU1TZkPymCw/English.png" alt="DrNutrition" className="h-10" data-id="element-3405" data-mp="3r1dhe3bj5qnw" />
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition-colors" aria-label="Close menu" data-id="element-3406" data-mp="3my7b4vof3zhf">
            <XIcon className="w-6 h-6 text-gray-900" data-id="element-3407" data-mp="383azs9lbaw04" />
          </button>
        </div>
        {/* Menu Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-white" data-id="element-3408" data-mp="33b9fq1hwk0zj">
          {/* Welcome Section */}
          <div className="px-4 py-8 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100" data-id="element-3409" data-mp="2zbipbywafmvj">
            <div className="flex items-center gap-5" data-id="element-3410" data-mp="2ucnyqas2wu0v">
              <div className="w-16 h-16 rounded-2xl bg-[#1E2B55] flex items-center justify-center text-white shadow-lg shadow-blue-900/10" data-id="element-3411" data-mp="2pc9yvg0msqdx">
                <UserIcon className="w-8 h-8" data-id="element-3412" data-mp="2kehyrypu1xyn" />
              </div>
              <div data-id="element-3413" data-mp="2apummu01i69j">
                <p className="text-xl font-black text-gray-900 leading-tight" data-id="element-3414" data-mp="2aojg4mf7mkeq">
                  Welcome to <br data-id="element-3415" data-mp="2foxpc7sjs0ec" />
                  <span className="text-[#1E2B55]" data-id="element-3416" data-mp="2fpgwctgnt4dl">Dr. Nutrition</span>
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-3417" data-mp="21pxycthkb9f0">
                  <button onClick={openAuthModal} className="text-sm font-extrabold text-[#1E2B55] bg-blue-50 px-4 py-1.5 rounded-full active:scale-95 transition-transform" data-id="element-3418" data-mp="25q85wal067vz">
                    Sign In
                  </button>
                  <button onClick={openAuthModal} className="text-sm font-bold text-gray-500 hover:text-gray-700" data-id="element-3419" data-mp="2knqth9ly2f1u">
                    Register
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Language, Country, Currency Section */}
          <div className="p-4 border-b border-gray-200" data-id="element-3420" data-mp="3h6ga0gg31r9r">
            <div className="space-y-3" data-id="element-3421" data-mp="15qed4j80mbqe">
              {/* Language Selector */}
              <button onClick={() => setLanguageBottomSheetOpen(true)} className="flex items-center justify-between w-full" data-id="element-3422" data-mp="10rl16v8dmssc">
                <div className="flex items-center space-x-2" data-id="element-3423" data-mp="1ffpko1f978c6">
                  <GlobeIcon className="w-4 h-4 text-gray-500" data-id="element-3424" data-mp="1ah4nybz7n6sh" />
                  <span className="text-sm" data-id="element-3425" data-mp="mrtttdcd8anb">Language</span>
                </div>
                <div className="flex items-center text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded" data-id="element-3426" data-mp="mtwh3pm4vpoc">
                  {language === 'en' ? 'English' : 'Arabic'}
                  <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-3427" data-mp="wqre242rdi0i" />
                </div>
              </button>
            </div>
          </div>
          {/* Loyalty Program Card - Using LoyaltyCard component */}
          <div className="px-4 py-3 border-b border-gray-100" data-id="element-3428" data-mp="283lg72t36u7d">
            <LoyaltyCard progress={loyaltyProgress} variant="compact" onJoinNow={() => {
            onClose();
            setLoyaltyBottomSheetOpen(true);
          }} loyaltyData={loyaltyData} data-id="element-3429" data-mp="236lbowbvxzid" />
          </div>
          {/* Menu Categories */}
          <div className="p-4 border-b border-gray-100" data-id="element-3430" data-mp="agsp6w9rdi1s">
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-3431" data-mp="f80zduiwx7g0">
              <button onClick={() => handleCategoryClick('best-sellers')} className="flex flex-col items-center p-3 rounded-xl bg-amber-50 border border-amber-100 active:scale-95 transition-transform" data-id="element-3432" data-mp="f9lubl952see">
                <TrendingUpIcon className="w-5 h-5 text-amber-600 mb-1" data-id="element-3433" data-mp="t7k7inbq4isb" />
                <span className="text-[10px] font-bold text-amber-900" data-id="element-3434" data-mp="t6962u5r3n4d">
                  Best Sellers
                </span>
              </button>
              <button onClick={() => handleCategoryClick('new-arrivals')} className="flex flex-col items-center p-3 rounded-xl bg-blue-50 border border-blue-100 active:scale-95 transition-transform" data-id="element-3435" data-mp="2v0gqcwbvkpmz">
                <ShoppingBagIcon className="w-5 h-5 text-blue-600 mb-1" data-id="element-3436" data-mp="2sgtwjj6o6j9w" />
                <span className="text-[10px] font-bold text-blue-900" data-id="element-3437" data-mp="2nmpk4kemxj1f">
                  New Arrivals
                </span>
              </button>
              <button onClick={() => handleCategoryClick('deals-offers')} className="flex flex-col items-center p-3 rounded-xl bg-red-50 border border-red-100 active:scale-95 transition-transform" data-id="element-3438" data-mp="2dqcv3zu1btd0">
                <TagIcon className="w-5 h-5 text-red-600 mb-1" data-id="element-3439" data-mp="36fbcuvu8wths" />
                <span className="text-[10px] font-bold text-red-900" data-id="element-3440" data-mp="31ew30e96a44k">
                  Hot Deals
                </span>
              </button>
            </div>
            <button onClick={() => {
            onClose();
            navigate('/categories');
          }} className="w-full bg-[#1E2B55] text-white text-sm font-bold py-3.5 rounded-xl shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2" data-id="element-3441" data-mp="2wiylw5q34tqq">
              <LayoutGridIcon className="w-4 h-4" data-id="element-3442" data-mp="1x491ayq8lma" />
              View All Categories
            </button>
          </div>
          {/* Shop By Categories */}
          <div className="p-4 border-b border-gray-200" data-id="element-3443" data-mp="gn2327jc85je">
            <h3 className="text-xs font-bold text-gray-500 mb-4 tracking-wider uppercase" data-id="element-3444" data-mp="btqrxu7ht2lo">
              Shop by Category
            </h3>
            <div className="grid grid-cols-1 gap-1" data-id="element-3445" data-mp="pnrvd19e3dsd">
              {[{
              name: 'Medicines (OTC)',
              slug: 'medicines',
              icon: <PillIcon className="w-5 h-5 text-blue-600" data-id="element-3446" data-mp="zjbm7993jma9" />
            }, {
              name: 'Vitamins & Supplements',
              slug: 'vitamins-supplements',
              icon: <ActivityIcon className="w-5 h-5 text-green-600" data-id="element-3447" data-mp="31dkdcpdjuuex" />
            }, {
              name: 'Personal Care',
              slug: 'personal-care',
              icon: <UserIcon className="w-5 h-5 text-purple-600" data-id="element-3448" data-mp="1bv477twqc61h" />
            }, {
              name: 'Mother & Baby',
              slug: 'mother-baby',
              icon: <BabyIcon className="w-5 h-5 text-pink-600" data-id="element-3449" data-mp="123zt0wevop7p" />
            }, {
              name: 'Skin Care',
              slug: 'skin-care',
              icon: <SparklesIcon className="w-5 h-5 text-rose-500" data-id="element-3450" data-mp="t31q6hg6enqp" />
            }, {
              name: 'Hair Care',
              slug: 'hair-care',
              icon: <DropletsIcon className="w-5 h-5 text-cyan-500" data-id="element-3451" data-mp="j6gg3hpaumg0" />
            }, {
              name: 'Medical Devices',
              slug: 'medical-devices',
              icon: <StethoscopeIcon className="w-5 h-5 text-slate-600" data-id="element-3452" data-mp="2djg2bto6qew9" />
            }].map(category => <button key={category.slug} onClick={() => handleCategoryClick(category.slug)} className="flex items-center py-3.5 px-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors w-full text-left group" data-id="element-3453" data-mp="2ii99738yv7ng">
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center mr-4 group-active:scale-95 transition-transform" data-id="element-3454" data-mp="273y2oewori41">
                    {category.icon}
                  </div>
                  <span className="text-sm font-bold text-gray-800 flex-1" data-id="element-3455" data-mp="2zrbrfpq8d7d2">
                    {category.name}
                  </span>
                  <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" data-id="element-3456" data-mp="2q2204c37nsq0" />
                </button>)}
            </div>
          </div>
          {/* Browse by Condition Section */}
          <div className="p-4 border-b border-gray-100" data-id="element-3457" data-mp="3ijmov4248d3s">
            <h3 className="text-xs font-bold text-gray-500 mb-4 tracking-wider uppercase" data-id="element-3458" data-mp="3drwdcurfzjas">
              Browse by Condition
            </h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-3459" data-mp="10a9zq92xvju7">
              {conditions.map((condition, index) => <button key={index} onClick={() => handleConditionClick(condition.slug)} className="flex flex-col items-center justify-center p-4 rounded-2xl bg-gray-50 border border-gray-100 active:bg-blue-50 active:border-blue-100 transition-all group" data-id="element-3460" data-mp="158v3ujlbannx" data-mpi={index}>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm group-active:scale-90 transition-transform" data-id="element-3461" data-mp="1j603a0qisoa7">
                    {condition.icon}
                  </div>
                  <span className="text-[11px] font-bold text-gray-700 text-center leading-tight" data-id="element-3462" data-mp="1wz9bvz6n95ro">
                    {condition.name}
                  </span>
                </button>)}
            </div>
          </div>
          {/* Account Section */}
          <div className="p-4 border-b border-gray-200" data-id="element-3463" data-mp="26xymd6w7nnxw">
            <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3464" data-mp="1ta6j35sj11o9">MY ACCOUNT</h3>
            <ul className="space-y-1" data-id="element-3465" data-mp="1oc54ax24voil">
              <li data-id="element-3466" data-mp="1ob3lu7isfzqu">
                <button onClick={() => {
                onClose();
                navigate('/account');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3467" data-mp="1o9slyisgrc50">
                  <UserIcon className="w-4 h-4 mr-3" data-id="element-3468" data-mp="1acm3yqfkdhgu" />
                  <span className="text-sm" data-id="element-3469" data-mp="15dizp87erf9x">My Account</span>
                </button>
              </li>
              <li data-id="element-3470" data-mp="1kat98c0hqb30">
                <button onClick={() => {
                onClose();
                navigate('/account/orders');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3471" data-mp="1kbbaxo5x9hib">
                  <PackageIcon className="w-4 h-4 mr-3" data-id="element-3472" data-mp="2qr5cusm30l5z" />
                  <span className="text-sm" data-id="element-3473" data-mp="2qsoo24svs60w">My Orders</span>
                </button>
              </li>
              <li data-id="element-3474" data-mp="338y70ixg3plt">
                <button onClick={() => {
                onClose();
                navigate('/account/payment-requests');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3475" data-mp="339hd92dqgpk0">
                  <CreditCardIcon className="w-4 h-4 mr-3" data-id="element-3476" data-mp="3gzumd82d2aax" />
                  <span className="text-sm" data-id="element-3477" data-mp="3h2fpe0eeawa9">Payment Requests</span>
                </button>
              </li>
              <li data-id="element-3478" data-mp="3m1a5uvow48r7">
                <button onClick={() => {
                onClose();
                navigate('/account/wishlist');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3479" data-mp="388aqctp3yejn">
                  <HeartIcon className="w-4 h-4 mr-3" data-id="element-3480" data-mp="8kavugt2r5zk" />
                  <span className="text-sm" data-id="element-3481" data-mp="8m439ybihu8m">My Wishlist</span>
                </button>
              </li>
            </ul>
          </div>
          {/* Help & Support */}
          <div className="p-4 border-b border-gray-200" data-id="element-3482" data-mp="eeohdw9efo6v">
            <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3483" data-mp="ed3mu6n34590">
              HELP & SUPPORT
            </h3>
            <ul className="space-y-1" data-id="element-3484" data-mp="4gqqf105ndak">
              <li data-id="element-3485" data-mp="4fp8qdomvsjv">
                <button onClick={() => {
                onClose();
                navigate('/contact-us');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3486" data-mp="4gifvfri8k2j">
                  <span className="text-sm" data-id="element-3487" data-mp="3mvj687td381y">Contact Us</span>
                </button>
              </li>
              <li data-id="element-3488" data-mp="3hx7yq3qjh9mw">
                <button onClick={() => {
                onClose();
                navigate('/faq');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3489" data-mp="3htk5l5p0ap8p">
                  <span className="text-sm" data-id="element-3490" data-mp="2xt0nl6god9hy">FAQs</span>
                </button>
              </li>
              <li data-id="element-3491" data-mp="32r26nre3p8tf">
                <button onClick={() => {
                onClose();
                navigate('/shipping-delivery');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3492" data-mp="32uq2wyaxk9bu">
                  <span className="text-sm" data-id="element-3493" data-mp="2f4vnqa3528of">Shipping & Delivery</span>
                </button>
              </li>
              <li data-id="element-3494" data-mp="2jzsxjj88sm2d">
                <button onClick={() => {
                onClose();
                navigate('/return-policy');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3495" data-mp="2p1ibb6mundu5">
                  <span className="text-sm" data-id="element-3496" data-mp="21bnz8racttay">Return Policy</span>
                </button>
              </li>
            </ul>
          </div>
          {/* Services & Tools */}
          <div className="p-4 border-b border-gray-200" data-id="element-3497" data-mp="3fie4b1vxdolq">
            <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3498" data-mp="3bhaqsm1co4no">
              SERVICES & TOOLS
            </h3>
            <ul className="space-y-1" data-id="element-3499" data-mp="3ph3cg2y0ruos">
              <li data-id="element-3500" data-mp="3pdxmymlhaqsj">
                <button onClick={() => {
                onClose();
                navigate('/prescription');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3501" data-mp="3khgzzvq0mih1">
                  <span className="text-sm" data-id="element-3502" data-mp="c1e2gismaa6f">Upload Prescription</span>
                </button>
              </li>
              <li data-id="element-3503" data-mp="72kpcrh8sl6s">
                <button onClick={() => {
                onClose();
                navigate('/prescription-history');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3504" data-mp="7215v6by1oqn">
                  <span className="text-sm" data-id="element-3505" data-mp="10m0pf5wwmg4d">Prescription History</span>
                </button>
              </li>
              <li data-id="element-3506" data-mp="14mu8nlzej6qx">
                <button onClick={() => {
                onClose();
                navigate('/branches');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3507" data-mp="14nc78p9jdx20">
                  <span className="text-sm" data-id="element-3508" data-mp="gxhu06lb26h8">Our Branches</span>
                </button>
              </li>
              <li data-id="element-3509" data-mp="lqkoqi1tjlz0">
                <button onClick={() => {
                onClose();
                navigate('/doctor-consultation');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3510" data-mp="lr47g0zamee3">
                  <span className="text-sm" data-id="element-3511" data-mp="3u5l4ccjvh25u">Doctor Consultation</span>
                </button>
              </li>
              <li data-id="element-3512" data-mp="30crggksvpqq">
                <button onClick={() => {
                onClose();
                navigate('/medication-finder');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3513" data-mp="23eal6seq1du">
                  <span className="text-sm" data-id="element-3514" data-mp="3t9fmfhk74z0j">Medication Finder</span>
                </button>
              </li>
            </ul>
          </div>
          {/* Legal & Policies */}
          <div className="p-4 border-b border-gray-200" data-id="element-3515" data-mp="kvp7mk0dr541">
            <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3516" data-mp="prnshxmi24zp">
              LEGAL & POLICIES
            </h3>
            <ul className="space-y-1" data-id="element-3517" data-mp="b319nlxnyv3q">
              <li data-id="element-3518" data-mp="g2w41zgne2tj">
                <button onClick={() => {
                onClose();
                navigate('/privacy-policy');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3519" data-mp="g2dygc83ic8y">
                  <span className="text-sm" data-id="element-3520" data-mp="2t9c7kdpabfuj">Privacy Policy</span>
                </button>
              </li>
              <li data-id="element-3521" data-mp="2ob0yi56td9dd">
                <button onClick={() => {
                onClose();
                navigate('/terms-conditions');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3522" data-mp="2o9hhudh8wwb2">
                  <span className="text-sm" data-id="element-3523" data-mp="2a9ybnq7zvv1e">Terms & Conditions</span>
                </button>
              </li>
              <li data-id="element-3524" data-mp="25coko64yjbbg">
                <button onClick={() => {
                onClose();
                navigate('/refund-policy');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3525" data-mp="25d7xx9jnxbj5">
                  <span className="text-sm" data-id="element-3526" data-mp="3up083a9dhimx">Refund Policy</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
        {/* Bottom Section */}
        <div className="p-4 border-t border-gray-200" data-id="element-3527" data-mp="2nd0uumi9cqli">
          <a href="#" className="flex items-center text-red-500 font-medium hover:text-red-600" data-id="element-3528" data-mp="2ngge81iyslsq">
            <LogOutIcon className="w-4 h-4 mr-2" data-id="element-3529" data-mp="2xc04a7aukq9k" />
            <span className="text-sm" data-id="element-3530" data-mp="2xa5hsojxgq3d">Sign Out</span>
          </a>
        </div>
      </div>
      {/* Language Bottom Sheet */}
      <LanguageBottomSheet isOpen={languageBottomSheetOpen} onClose={() => setLanguageBottomSheetOpen(false)} currentLanguage={language} onLanguageChange={handleLanguageChange} data-id="element-3531" data-mp="24nuj5jfvsz97" />
      {/* Auth Bottom Sheet */}
      <AuthBottomSheet isOpen={authBottomSheetOpen} onClose={() => setAuthBottomSheetOpen(false)} onAuthSuccess={handleAuthSuccess} data-id="element-3532" data-mp="2dmpqwzykqhem" />
      {/* Loyalty Onboarding Bottom Sheet */}
      <LoyaltyOnboardingBottomSheet isOpen={loyaltyBottomSheetOpen} onClose={() => setLoyaltyBottomSheetOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-3533" data-mp="1lwup43tj4z92" />
    </>;
}